import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const tableOfContents = [
  { id: 'who-is-sweetpilot-designed-for', title: 'Who is SweetPilot designed for?' },
  { id: 'key-features', title: 'Key Features' },
  { id: 'brain-ai', title: 'Brain AI', indent: true },
  { id: 'ai-employees', title: 'AI Employees', indent: true },
  { id: 'automations', title: 'Automations', indent: true },
  { id: 'inbox-history', title: 'Inbox & history', indent: true },
  { id: 'settings-billing', title: 'Settings & billing', indent: true },
  { id: 'referral-program', title: 'Referral program' },
  { id: 'faq', title: 'FAQ' },
];

const relatedArticles = [
  { title: 'How to navigate the SweetPilot web app', href: '/doc/article/navigate-sweetpilot' },
  { title: 'SweetPilot AI Employees explained', href: '/doc/article/ai-employees-explained' },
  { title: 'Which AI Models does SweetPilot use?', href: '/doc/article/ai-models' },
  { title: 'Brain AI explained', href: '/doc/article/brain-ai-explained' },
  { title: 'How to get started with SweetPilot', href: '/doc/article/getting-started' },
];

const faqItems = [
  {
    question: 'What AI Models are Used in SweetPilot App?',
    answer: 'SweetPilot uses a combination of state-of-the-art AI models including GPT-4, Claude, and custom fine-tuned models optimized for specific tasks like customer support, content creation, and data analysis.',
  },
  {
    question: 'Can I generate a sequence of images using the same theme?',
    answer: 'Yes, you can maintain consistent themes across image generations by using the same style parameters and referencing previous outputs in your prompts.',
  },
  {
    question: 'Are these AI Employees the same as ChatGPT?',
    answer: 'While SweetPilot AI Employees are powered by similar underlying technology, they are specialized assistants with unique personas, skill sets, and power-ups designed for specific business tasks. Unlike general-purpose chatbots, each AI Employee is optimized for their domain.',
  },
];

export default function WhatIsSweetPilot() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [isAtTop, setIsAtTop] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      // Check if at top of page (before first section)
      const firstSection = sections[0];
      if (firstSection && scrollPosition < firstSection.offsetTop) {
        setIsAtTop(true);
        setActiveSection('');
        return;
      }

      setIsAtTop(false);

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tableOfContents[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // SEO Schema markup
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'What is SweetPilot? - AI Employee Platform for Business',
    description: 'Learn about SweetPilot - the AI employee platform that helps businesses automate tasks with AI-powered assistants, Brain AI, and smart automations.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: { '@type': 'Organization', name: 'SweetPilot', logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' } },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sweetpilot.com/doc/article/what-is-sweetpilot' }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer }
    }))
  };

  return (
    <>
      <Helmet>
        <title>What is SweetPilot? - AI Employee Platform for Business Automation | SweetPilot</title>
        <meta name="description" content="Discover SweetPilot - the AI employee platform that helps businesses automate tasks, manage workflows, and boost productivity with AI-powered assistants and Brain AI technology." />
        <meta name="keywords" content="SweetPilot, AI employees, AI assistants, business automation, Brain AI, AI platform, workflow automation, AI chatbot, virtual employees, AI productivity tools" />
        <meta property="og:title" content="What is SweetPilot? - AI Employee Platform" />
        <meta property="og:description" content="Learn about SweetPilot - the AI employee platform with Brain AI, AI Employees, automations, and integrations for business productivity." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/what-is-sweetpilot" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="What is SweetPilot? - AI Employee Platform" />
        <meta name="twitter:description" content="Discover SweetPilot - AI employees, Brain AI, and smart automations for business." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/what-is-sweetpilot" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <StaticPageLayout
        title=""
        subtitle=""
        showBackButton={false}
      >
        <div className="flex gap-16 lg:gap-20">
          {/* Main Content */}
          <div className="flex-1 max-w-2xl">
            {/* Breadcrumbs */}
            <nav className="mb-8">
            <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              <li>
                <Link
                  to="/docs"
                  className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                >
                  All Collections
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li>
                <Link
                  to="/doc/collections/getting-started"
                  className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                >
                  Getting started
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li className={isDark ? 'text-white' : 'text-slate-900'}>
                What is SweetPilot?
              </li>
            </ol>
          </nav>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              What is SweetPilot?
            </h1>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              A digital assistant platform combining AI employees, Brain AI knowledge base, and automation workflows.
            </p>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}
          >
            <p className={`text-lg leading-relaxed mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot is a digital assistant platform that combines multiple AI "employees," a central Brain AI knowledge base and several automation workflows. It is designed to streamline business processes ranging from customer support and copywriting to data analysis, recruitment and social media management. Each employee is specialized for a particular domain (e.g., Nova handles support queries, Atlas performs data analysis, Echo writes copy). Users interact with employees through chat-like conversations and can customize the tone, length and language of responses.
            </p>

            {/* Who is SweetPilot designed for? */}
            <h2 id="who-is-sweetpilot-designed-for" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Who is SweetPilot designed for?
            </h2>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot is designed for people wanting to:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Utilize AI to work more efficiently and save time without having technical knowledge.</li>
              <li>Manage multiple businesses or projects in one place.</li>
              <li>Manage their single business or project in a single place.</li>
            </ul>
            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              When building SweetPilot, we think of small business owners, agencies and freelancers; however, it can be utilized by anyone working on their business, project or simply wanting to automate their workflows.
            </p>

            {/* Key Features */}
            <h2 id="key-features" className={`text-2xl font-bold mt-12 mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Key Features
            </h2>

            {/* Brain AI */}
            <h3 id="brain-ai" className={`text-xl font-semibold mt-8 mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Brain AI
            </h3>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              A knowledge management module that stores snippets, webpages and media; it personalizes employee responses based on your business information and integrates with external accounts.{' '}
              <Link
                to="/doc/collections/brain-ai"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                Learn about Brain AI
              </Link>.
            </p>

            {/* AI Employees */}
            <h3 id="ai-employees" className={`text-xl font-semibold mt-8 mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              AI Employees
            </h3>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              15 employees, each with a unique persona and skill set. They can draft emails, write blog posts, analyze data, brainstorm product ideas, perform SEO audits and more. Some employees have automations that can run in the background and all of our employees have their own set of power-ups that act as instant tasks.{' '}
              <Link
                to="/employees"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                Learn about SweetPilot's AI Employees
              </Link>.
            </p>

            {/* Automations */}
            <h3 id="automations" className={`text-xl font-semibold mt-8 mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Automations
            </h3>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Automations are standalone workflows that operate outside of the chat environment. They run in the background after setup, later requiring minimal input from your side. On the Dashboard page some employee cards show a zap icon badge in the top right corner; clicking that badge routes you to an automation setup page for that employee. Unlike power-ups, automations require a quick setup process and rely on integrations.{' '}
              <Link
                to="/doc/collections/automations"
                className={`font-medium underline ${isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'}`}
              >
                Learn about available automations
              </Link>.
            </p>

            {/* Inbox & history */}
            <h3 id="inbox-history" className={`text-xl font-semibold mt-8 mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Inbox & history
            </h3>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              A unified Inbox for ideas generated by employees, completed tasks awaiting your review in automations, and a Chat History panel for your past conversations with AI Employees.
            </p>

            {/* Settings & billing */}
            <h3 id="settings-billing" className={`text-xl font-semibold mt-8 mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Settings & billing
            </h3>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              A page for personal details, language preferences, password and subscription management, and experimental features.
            </p>

            {/* Referral program */}
            <h2 id="referral-program" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Referral program
            </h2>
            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Share your unique code to give and get free access to SweetPilot for a month.
            </p>

            {/* FAQ */}
            <h2 id="faq" className={`text-2xl font-bold mt-12 mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              FAQ
            </h2>
            <div className="space-y-3 mb-12">
              {faqItems.map((item, index) => (
                <div
                  key={index}
                  className={`rounded-xl border overflow-hidden ${
                    isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
                  }`}
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className={`w-full flex items-center gap-3 p-4 text-left font-medium ${
                      isDark ? 'text-white hover:bg-white/5' : 'text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    <span className={`transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`}>▶</span>
                    {item.question}
                  </button>
                  {expandedFaq === index && (
                    <div className={`px-4 pb-4 pl-10 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {item.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Divider */}
            <hr className={`my-12 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

            {/* Related Articles */}
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Related Articles
            </h2>
            <div className={`rounded-2xl border overflow-hidden ${
              isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
            }`}>
              {relatedArticles.map((article, index) => (
                <Link
                  key={index}
                  to={article.href}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    index !== relatedArticles.length - 1 ? (isDark ? 'border-b border-white/5' : 'border-b border-slate-100') : ''
                  } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                >
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {article.title}
                  </span>
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                </Link>
              ))}
            </div>

            {/* Feedback Section */}
            <div className={`mt-12 p-8 rounded-2xl text-center ${
              isDark ? 'bg-dark-800/50 border border-white/10' : 'bg-slate-50 border border-slate-200'
            }`}>
              <p className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Did this answer your question?
              </p>
              <div className="flex justify-center gap-4">
                <button className="text-3xl hover:scale-110 transition-transform" title="No">😞</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat">😐</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Yes">😃</button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Sidebar - Table of Contents */}
        <aside className="hidden lg:block w-56 shrink-0 ml-auto">
          <div className="sticky top-24 pl-6 border-l border-slate-200 dark:border-white/10">
            {/* Sidebar Title */}
            <h3 className={`text-base mb-4 ${
              isAtTop
                ? isDark ? 'text-white font-semibold' : 'text-slate-900 font-semibold'
                : isDark ? 'text-gray-400' : 'text-slate-600'
            }`}>
              What is SweetPilot?
            </h3>
            <nav className="space-y-2">
              {tableOfContents.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left py-1.5 text-sm transition-colors ${
                    item.indent ? 'pl-4' : ''
                  } ${
                    activeSection === item.id
                      ? isDark
                        ? 'text-white font-medium'
                        : 'text-slate-900 font-medium'
                      : isAtTop && index === 0
                        ? isDark
                          ? 'text-white font-medium'
                          : 'text-slate-900 font-medium'
                        : isDark
                          ? 'text-gray-500 hover:text-gray-300'
                          : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {item.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center mt-16"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">🚀</span>
        </div>
        <a
          href="mailto:help@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          help@sweetpilot.com
        </a>
      </motion.div>
    </StaticPageLayout>
    </>
  );
}
